// ABOUTME: Header of the treeview catalog root: the user's favorite families and items, then the families title.
// ABOUTME: Fills the model-selection familyListHeader slot; renders nothing once a family is opened or a search runs.

import type { ICategory, IProduct } from '@coolshop/cool-model-selection';
import { useModelSelection } from '@coolshop/cool-model-selection';
import { Box, Button, Card, CardContent, SectionHeader, Stack, Typography } from '@ds/coolshop';
import { useFavoritesEnabled } from '@src/_new/modules/user-favorites';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useCatalogFavorites } from './CatalogFavoritesContext';
import { CatalogThumbnail } from './CatalogThumbnail';
import { collectCatalogFavorites, FavoriteEntry } from './collectCatalogFavorites';
import { getProductImageUrl } from './getProductImageUrl';

// How many favorites the strip shows before "View all", and per row once they are all shown.
const COLLAPSED_FAVORITES = 4;
const FAVORITES_PER_ROW = 4;

interface CatalogRootHeaderProps {
    /** Opens a favorite item, as a click on its card in the catalog does. */
    onProductSelect: (product: IProduct) => void;
}

export function CatalogRootHeader({ onProductSelect }: CatalogRootHeaderProps) {
    const { formatMessage } = useIntl();
    const { data, group, gamma, range, searchText, actions } = useModelSelection();
    const favoritesEnabled = useFavoritesEnabled();
    const catalogFavorites = useCatalogFavorites();
    const [expanded, setExpanded] = useState(false);

    const favorites = useMemo(
        () =>
            favoritesEnabled && catalogFavorites
                ? collectCatalogFavorites(data.categories ?? [], catalogFavorites.isFavorite)
                : [],
        [favoritesEnabled, catalogFavorites, data.categories],
    );

    if (group || gamma || range || searchText) {
        return null;
    }

    const kindLabel = (entry: FavoriteEntry) => {
        if (entry.kind === 'product') {
            return formatMessage({ id: 'cpq.catalog.favorites.item', defaultMessage: 'Item' });
        }
        return entry.path.length === 1
            ? formatMessage({ id: 'cpq.catalog.favorites.family', defaultMessage: 'Family' })
            : formatMessage({ id: 'cpq.catalog.favorites.series', defaultMessage: 'Series' });
    };

    // Opens a favorite family or series the way the family cards do: every level of its path.
    const openCategory = (path: ICategory[]) => {
        path.forEach(step => {
            if (step.type === 'group') actions.setGroup(step);
            if (step.type === 'gamma') actions.setGamma(step);
            if (step.type === 'range') actions.setRange(step);
        });
        actions.setSearchTags(path);
    };

    const shown = expanded ? favorites : favorites.slice(0, COLLAPSED_FAVORITES);

    return (
        <Stack gap={3}>
            {favorites.length > 0 && (
                <Stack gap={2} data-testid="catalog-favorites">
                    {/* The configurator's section heading, as over its feature groups. */}
                    <SectionHeader
                        label={formatMessage({
                            id: 'cpq.catalog.favorites.title',
                            defaultMessage: 'My favorites',
                        })}
                        divider={false}
                        endAdornment={
                            favorites.length > COLLAPSED_FAVORITES && (
                                <Button size="small" onClick={() => setExpanded(prev => !prev)}>
                                    {expanded
                                        ? formatMessage({
                                              id: 'cpq.catalog.favorites.showLess',
                                              defaultMessage: 'Show less',
                                          })
                                        : formatMessage({
                                              id: 'cpq.catalog.favorites.viewAll',
                                              defaultMessage: 'View all',
                                          })}
                                </Button>
                            )
                        }
                    />
                    <Box
                        display="grid"
                        // Four per row, "View all" included; fewer than four share the whole width.
                        gridTemplateColumns={`repeat(${Math.min(
                            shown.length,
                            FAVORITES_PER_ROW,
                        )}, minmax(0, 1fr))`}
                        gap={2}
                    >
                        {shown.map(entry => {
                            const name =
                                entry.kind === 'product'
                                    ? entry.product.description
                                    : entry.category.description;
                            const target =
                                entry.kind === 'product' ? entry.product : entry.category;
                            return (
                                <Card
                                    key={`${entry.kind}-${target.id}`}
                                    cursor="pointer"
                                    data-testid={`catalog-favorite-${entry.kind}`}
                                    onClick={() =>
                                        entry.kind === 'product'
                                            ? onProductSelect(entry.product)
                                            : openCategory(entry.path)
                                    }
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" gap={2}>
                                            <CatalogThumbnail
                                                url={getProductImageUrl(target)}
                                                alt={name}
                                                size="48px"
                                            />
                                            <Stack minWidth={0}>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    {kindLabel(entry)}
                                                </Typography>
                                                <Typography variant="subtitle2">{name}</Typography>
                                            </Stack>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                </Stack>
            )}
            <SectionHeader
                label={formatMessage({
                    id: 'cpq.catalog.allFamilies',
                    defaultMessage: 'All families',
                })}
                divider={false}
            />
        </Stack>
    );
}
